// Urdu-first symptom intake (epic E09 / proposal Section 07 "Urdu NLP intake").
// Free text in Urdu script, Roman Urdu or English is mapped to structured findings coded in
// SNOMED CT and ICD-10. This is a deterministic, offline-capable baseline; the `IntakeEngine`
// interface lets a model-based engine replace it later without changing callers.

export interface SymptomConcept {
  id: string;
  en: string;
  ur: string;
  snomed: string;
  icd10: string;
  redFlag: boolean;
  keywords: string[];
}

export const SYMPTOMS: SymptomConcept[] = [
  { id: 'fever', en: 'Fever', ur: 'بخار', snomed: '386661006', icd10: 'R50.9', redFlag: false, keywords: ['بخار', 'bukhar', 'bukhaar', 'bukar', 'fever', 'feverish'] },
  { id: 'cough', en: 'Cough', ur: 'کھانسی', snomed: '49727002', icd10: 'R05', redFlag: false, keywords: ['کھانسی', 'khansi', 'khaansi', 'khasi', 'cough'] },
  { id: 'headache', en: 'Headache', ur: 'سر درد', snomed: '25064002', icd10: 'R51', redFlag: false, keywords: ['سر درد', 'سر میں درد', 'sar dard', 'sir dard', 'sar mein dard', 'headache', 'head ache'] },
  { id: 'chest_pain', en: 'Chest pain', ur: 'سینے میں درد', snomed: '29857009', icd10: 'R07.4', redFlag: true, keywords: ['سینے میں درد', 'سینے درد', 'سینہ درد', 'seene mein dard', 'seenay mein dard', 'seene me dard', 'seena dard', 'chest pain', 'chest tightness'] },
  { id: 'dyspnoea', en: 'Shortness of breath', ur: 'سانس پھولنا', snomed: '267036007', icd10: 'R06.0', redFlag: true, keywords: ['سانس پھول', 'سانس لینے میں', 'سانس کی تکلیف', 'saans phool', 'sans phool', 'saans lene mein', 'breathless', 'shortness of breath', 'difficulty breathing', 'cannot breathe'] },
  { id: 'abdominal_pain', en: 'Abdominal pain', ur: 'پیٹ میں درد', snomed: '21522001', icd10: 'R10.4', redFlag: false, keywords: ['پیٹ میں درد', 'پیٹ درد', 'pait dard', 'pet dard', 'pait mein dard', 'pet mein dard', 'stomach pain', 'abdominal pain', 'tummy ache'] },
  { id: 'diarrhoea', en: 'Diarrhoea', ur: 'دست', snomed: '62315008', icd10: 'R19.7', redFlag: false, keywords: ['دست', 'ڈائریا', 'پیچش', 'dast', 'loose motion', 'diarrhea', 'diarrhoea'] },
  { id: 'vomiting', en: 'Vomiting', ur: 'قے / الٹی', snomed: '422400008', icd10: 'R11.1', redFlag: false, keywords: ['قے', 'الٹی', 'ulti', 'qay', 'vomit', 'vomiting'] },
  { id: 'dizziness', en: 'Dizziness', ur: 'چکر آنا', snomed: '404640003', icd10: 'R42', redFlag: false, keywords: ['چکر', 'chakkar', 'chakar', 'dizzy', 'dizziness'] },
  { id: 'fatigue', en: 'Weakness / tiredness', ur: 'کمزوری / تھکاوٹ', snomed: '84229001', icd10: 'R53.83', redFlag: false, keywords: ['کمزوری', 'تھکاوٹ', 'تھکن', 'kamzori', 'thakawat', 'thakan', 'weakness', 'tired', 'fatigue'] },
  { id: 'joint_pain', en: 'Joint pain', ur: 'جوڑوں میں درد', snomed: '57676002', icd10: 'M25.50', redFlag: false, keywords: ['جوڑوں میں درد', 'جوڑوں کا درد', 'ghutnon', 'گھٹنوں', 'joron mein dard', 'joint pain', 'knee pain'] },
  { id: 'back_pain', en: 'Back pain', ur: 'کمر درد', snomed: '161891005', icd10: 'M54.9', redFlag: false, keywords: ['کمر درد', 'کمر میں درد', 'kamar dard', 'kamar mein dard', 'back pain', 'backache'] },
  { id: 'sore_throat', en: 'Sore throat', ur: 'گلے میں درد', snomed: '162397003', icd10: 'R07.0', redFlag: false, keywords: ['گلے میں درد', 'گلا خراب', 'gale mein dard', 'gala kharab', 'sore throat', 'throat pain'] },
  { id: 'rash', en: 'Skin rash', ur: 'جلد پر دانے', snomed: '271807003', icd10: 'R21', redFlag: false, keywords: ['دانے', 'خارش', 'daane', 'kharish', 'rash', 'itching'] },
  { id: 'dysuria', en: 'Burning on passing urine', ur: 'پیشاب میں جلن', snomed: '49650001', icd10: 'R30.0', redFlag: false, keywords: ['پیشاب میں جلن', 'peshab mein jalan', 'burning urine', 'painful urination'] },
  { id: 'thirst', en: 'Excessive thirst', ur: 'زیادہ پیاس', snomed: '17173007', icd10: 'R63.1', redFlag: false, keywords: ['زیادہ پیاس', 'بہت پیاس', 'pyas', 'piyas', 'excessive thirst', 'very thirsty'] },
  { id: 'syncope', en: 'Fainting / loss of consciousness', ur: 'بے ہوشی', snomed: '271594007', icd10: 'R55', redFlag: true, keywords: ['بے ہوش', 'بےہوش', 'behosh', 'be hosh', 'fainted', 'fainting', 'unconscious', 'passed out'] },
  { id: 'bleeding', en: 'Bleeding', ur: 'خون بہنا', snomed: '131148009', icd10: 'R58', redFlag: true, keywords: ['خون بہ', 'خون آ', 'khoon beh', 'khoon aa', 'bleeding', 'blood loss'] },
  { id: 'one_sided_weakness', en: 'Sudden weakness on one side / face drooping', ur: 'اچانک ایک طرف کمزوری یا چہرہ ٹیڑھا', snomed: '278286009', icd10: 'R29.810', redFlag: true, keywords: ['ایک طرف کمزوری', 'چہرہ ٹیڑھا', 'فالج', 'falij', 'one side weakness', 'face drooping', 'stroke'] },
];

const NEGATIONS = ['نہیں', 'نہی', 'بغیر', 'nahi', 'nahin', 'nai', 'no', 'not', 'without', 'denies', 'never'];

export interface SymptomFinding {
  id: string;
  en: string;
  ur: string;
  snomed: string;
  icd10: string;
  redFlag: boolean;
  negated: boolean;
  evidence: string;
}

export interface IntakeResult {
  engine: string;
  text: string;
  language: 'ur' | 'roman-ur' | 'en' | 'mixed';
  findings: SymptomFinding[];
  durationDays?: number;
}

export interface IntakeEngine {
  name: string;
  extract(text: string): IntakeResult | Promise<IntakeResult>;
}

const URDU_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

export function normaliseText(input: string): string {
  let s = input.toLowerCase();
  s = s.replace(/[۰-۹]/g, (d) => String(URDU_DIGITS.indexOf(d))).replace(/[٠-٩]/g, (d) => String(ARABIC_DIGITS.indexOf(d)));
  s = s
    .replace(/ي/g, 'ی')
    .replace(/ى/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/ە/g, 'ہ')
    .replace(/[ً-ْٰ]/g, ''); // strip harakat
  s = s.replace(/\s+/g, ' ');
  return s.trim();
}

function detectLanguage(s: string): IntakeResult['language'] {
  const urdu = (s.match(/[؀-ۿ]/g) ?? []).length;
  const latin = (s.match(/[a-z]/g) ?? []).length;
  if (urdu && latin) return 'mixed';
  if (urdu) return 'ur';
  const english = /\b(pain|have|fever|cough|days?|since|and|the|my)\b/.test(s);
  return english ? 'en' : 'roman-ur';
}

const NUMBER_WORDS: Record<string, number> = {
  ek: 1, aik: 1, do: 2, teen: 3, char: 4, chaar: 4, panch: 5, paanch: 5, chay: 6, che: 6, saat: 7, aath: 8, nau: 9, das: 10,
  ایک: 1, دو: 2, تین: 3, چار: 4, پانچ: 5, چھ: 6, سات: 7, آٹھ: 8, نو: 9, دس: 10,
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
};

const UNIT_DAYS: [RegExp, number][] = [
  [/^(دن|din|day|days|روز|roz)/, 1],
  [/^(ہفتے|ہفتہ|ہفتوں|hafte|hafta|haftay|week|weeks)/, 7],
  [/^(مہینے|مہینہ|mahine|mahina|month|months)/, 30],
];

function extractDuration(s: string): number | undefined {
  const tokens = s.split(/[\s،,.۔]+/).filter(Boolean);
  for (let i = 0; i < tokens.length - 1; i++) {
    const n = /^\d+$/.test(tokens[i]) ? Number(tokens[i]) : NUMBER_WORDS[tokens[i]];
    if (!n) continue;
    for (const [re, mult] of UNIT_DAYS) if (re.test(tokens[i + 1])) return n * mult;
  }
  return undefined;
}

function splitClauses(s: string): string[] {
  return s.split(/[،,.۔;!?\n]| اور | aur | and | lekin | لیکن | but /).map((c) => ` ${c.trim()} `).filter((c) => c.trim());
}

export const lexiconIntakeEngine: IntakeEngine = {
  name: 'movo-lexicon-ur-0.1',
  extract(text: string): IntakeResult {
    const norm = normaliseText(text);
    const clauses = splitClauses(norm);
    const found = new Map<string, SymptomFinding>();
    for (const clause of clauses) {
      const negated = NEGATIONS.some((n) => clause.includes(` ${n} `));
      for (const c of SYMPTOMS) {
        const hit = c.keywords.find((k) => {
          const kw = normaliseText(k);
          return /[a-z]/.test(kw) ? new RegExp(`(^|[^a-z])${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(clause) : clause.includes(kw);
        });
        if (!hit) continue;
        const prev = found.get(c.id);
        // A positive mention anywhere wins over a negated one.
        if (prev && !prev.negated) continue;
        found.set(c.id, { id: c.id, en: c.en, ur: c.ur, snomed: c.snomed, icd10: c.icd10, redFlag: c.redFlag, negated, evidence: clause.trim() });
      }
    }
    return { engine: this.name, text, language: detectLanguage(norm), findings: [...found.values()], durationDays: extractDuration(norm) };
  },
};

export function extractIntake(text: string): IntakeResult {
  return lexiconIntakeEngine.extract(text) as IntakeResult;
}
